/**
 * Sidebar v2 thread status model: the six-state visual status, the recede
 * rule, unseen-completion tracking, and the status pills (including the
 * project rollup).
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.logic.ts`
 * at pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: pill colours are re-expressed in forge's
 * token ladder (severity rungs and categorical hues) instead of upstream's
 * raw palette text classes, and the turn-settled check is local because
 * forge's session logic does not export one.
 */
import type { SidebarThreadSummary } from "~/types";
import { isSessionActivelyRunning, isSessionTransitioning } from "~/lib/threadRuntimeState";

// ── Sidebar thread status model ─────────────────────────────────────
// Five visual states, three colors: color is reserved for "act now"
// (approval), "in motion" (working), and "broken" (failed). Ready is the
// unlabeled resting state — the agent stopped and is waiting on the user,
// whether it finished, asked a question, or proposed a plan.
// Unread completion is tracked separately: it describes whether a ready
// thread needs attention, not what the thread is currently doing.
export type SidebarThreadStatus =
  | "approval"
  | "input"
  | "working"
  | "monitoring"
  | "failed"
  | "ready";

export type SidebarThreadStatusInput = Pick<
  SidebarThreadSummary,
  "hasPendingApprovals" | "hasPendingUserInput" | "session"
> & {
  backgroundLiveness?: "working" | "monitoring" | null | undefined;
};

/** Resolve row status with pending approvals/input first, then session activity or failure, then background liveness. */
export function resolveSidebarThreadStatus(thread: SidebarThreadStatusInput): SidebarThreadStatus {
  if (thread.hasPendingApprovals) {
    return "approval";
  }
  if (thread.hasPendingUserInput) {
    return "input";
  }
  // Forge's own live-session predicates: a running turn with an adopted
  // turn id is working; a connecting session is the "Connecting" state.
  if (isSessionActivelyRunning(thread.session)) {
    return "working";
  }
  // A failed session outranks lingering background liveness: the user must
  // see the failure, not a stale Working (review finding).
  if (thread.session?.status === "error") {
    return "failed";
  }
  if (isSessionTransitioning(thread.session)) {
    return "working";
  }
  // Background work outlives the turn: fleets read as working; monitoring
  // only when watch loops are the sole live work. Forge shells do not carry
  // the field yet, so the seam stays optional and defaults to none.
  if (thread.backgroundLiveness === "working") {
    return "working";
  }
  if (thread.backgroundLiveness === "monitoring") {
    return "monitoring";
  }
  return "ready";
}

/** Decide whether a row can be visually subdued from its status, unread/wake state, and current selection. */
export function shouldRecedeSidebarThread(input: {
  status: SidebarThreadStatus;
  isUnread: boolean;
  isWoke: boolean;
  isActive: boolean;
  isSelected: boolean;
}): boolean {
  if (input.isActive || input.isSelected || input.status === "input") return false;
  if (input.status === "working" || input.status === "monitoring") return true;
  if (input.status === "ready" || input.status === "approval") {
    return !input.isUnread && !input.isWoke;
  }
  return false;
}

type LatestTurnLike = { readonly completedAt: string | null } | null | undefined;

/** A turn has settled when it completed and no session is still adopting or
    running work. Local twin of upstream's session-logic helper, expressed
    over forge's session predicates. */
export function isLatestTurnSettled(
  latestTurn: LatestTurnLike,
  session:
    | {
        readonly status?: string | null;
        readonly activeTurnId?: string | null | undefined;
      }
    | null
    | undefined,
): boolean {
  if (!latestTurn?.completedAt) return false;
  return !isSessionActivelyRunning(session as never) && session?.status !== "connecting";
}

/** Compare completion and visit timestamps; an absent visit is not unread, while an invalid recorded visit is. */
export function hasUnseenCompletion(thread: {
  latestTurn: LatestTurnLike;
  lastVisitedAt?: string | undefined;
}): boolean {
  if (!thread.latestTurn?.completedAt) return false;
  const completedAt = Date.parse(thread.latestTurn.completedAt);
  if (Number.isNaN(completedAt)) return false;
  if (!thread.lastVisitedAt) return false;

  const lastVisitedAt = Date.parse(thread.lastVisitedAt);
  if (Number.isNaN(lastVisitedAt)) return true;
  return completedAt > lastVisitedAt;
}

/** The timestamp a working thread's elapsed label counts from: the running
    turn's start (request time until adoption), falling back to the session's
    last transition when the turn projection lags behind. Malformed
    timestamps fall through to the next candidate, not just missing ones. */
export function resolveWorkingStartedAt(
  thread: Pick<SidebarThreadSummary, "latestTurn" | "session">,
): string | null {
  const turn = thread.latestTurn;
  if (turn && turn.completedAt === null) {
    return firstValidTimestamp(turn.startedAt, turn.requestedAt, thread.session?.updatedAt);
  }
  return firstValidTimestamp(thread.session?.updatedAt);
}

/** Return the first parseable timestamp, skipping null, undefined, and invalid values. */
function firstValidTimestamp(
  ...candidates: ReadonlyArray<string | null | undefined>
): string | null {
  for (const candidate of candidates) {
    if (candidate == null) continue;
    if (!Number.isNaN(Date.parse(candidate))) return candidate;
  }
  return null;
}

export interface ThreadStatusPill {
  label:
    | "Working"
    | "Monitoring"
    | "Connecting"
    | "Completed"
    | "Pending Approval"
    | "Awaiting Input"
    | "Plan Ready";
  colorClass: string;
  dotClass: string;
  pulse: boolean;
}

// Rollup order mirrors the per-thread resolver exactly: attention states,
// then active work, then the actionable plan prompt, then passive
// monitoring. A Monitoring sibling must never hide a Plan Ready thread.
const THREAD_STATUS_PRIORITY: Record<ThreadStatusPill["label"], number> = {
  "Pending Approval": 6,
  "Awaiting Input": 5,
  Working: 4,
  Connecting: 4,
  "Plan Ready": 3,
  Monitoring: 2,
  Completed: 1,
};

type ThreadStatusPillInput = {
  hasPendingApprovals: boolean;
  hasPendingUserInput: boolean;
  hasActionableProposedPlan: boolean;
  interactionMode?: string | null | undefined;
  session: { readonly status?: string | null } | null;
  backgroundLiveness?: "working" | "monitoring" | null | undefined;
  latestTurn: LatestTurnLike;
  lastVisitedAt?: string | undefined;
};

/** Choose the highest-precedence actionable or activity pill, falling back to unseen completion or no pill. */
export function resolveThreadStatusPill(input: {
  thread: ThreadStatusPillInput;
}): ThreadStatusPill | null {
  const { thread } = input;

  if (thread.hasPendingApprovals) {
    return {
      label: "Pending Approval",
      colorClass: "text-warning-foreground",
      dotClass: "bg-warning",
      pulse: false,
    };
  }

  if (thread.hasPendingUserInput) {
    return {
      label: "Awaiting Input",
      colorClass: "text-hue-indigo-foreground",
      dotClass: "bg-hue-indigo",
      pulse: false,
    };
  }

  if (thread.session?.status === "running") {
    return {
      label: "Working",
      colorClass: "text-hue-sky-foreground",
      dotClass: "bg-hue-sky",
      pulse: true,
    };
  }

  if (thread.session?.status === "connecting") {
    return {
      label: "Connecting",
      colorClass: "text-hue-sky-foreground",
      dotClass: "bg-hue-sky",
      pulse: true,
    };
  }

  // An actionable plan prompt outranks lingering background work: it needs
  // the user's decision, while liveness merely reports (review finding).
  const hasPlanReadyPrompt =
    !thread.hasPendingUserInput &&
    thread.interactionMode === "plan" &&
    isLatestTurnSettled(thread.latestTurn, thread.session) &&
    thread.hasActionableProposedPlan;
  if (hasPlanReadyPrompt) {
    return {
      label: "Plan Ready",
      colorClass: "text-hue-violet-foreground",
      dotClass: "bg-hue-violet",
      pulse: false,
    };
  }

  // The turn can settle while native background work runs on. Subagent and
  // workflow fleets read as plain Working; Monitoring is reserved for watch
  // loops (a parent agent babysitting a PR, tailing checks) with no other
  // live work. Same recede treatment as Working per inbox-zero.
  if (thread.backgroundLiveness === "working") {
    return {
      label: "Working",
      colorClass: "text-hue-sky-foreground",
      dotClass: "bg-hue-sky",
      pulse: true,
    };
  }

  if (thread.backgroundLiveness === "monitoring") {
    return {
      label: "Monitoring",
      colorClass: "text-hue-sky-foreground",
      dotClass: "bg-hue-sky",
      pulse: false,
    };
  }

  if (hasUnseenCompletion(thread)) {
    return {
      label: "Completed",
      colorClass: "text-success-foreground",
      dotClass: "bg-success",
      pulse: false,
    };
  }

  return null;
}

/** Choose the highest-priority non-null thread pill to summarize a project. */
export function resolveProjectStatusIndicator(
  statuses: ReadonlyArray<ThreadStatusPill | null>,
): ThreadStatusPill | null {
  let highestPriorityStatus: ThreadStatusPill | null = null;

  for (const status of statuses) {
    if (status === null) continue;
    if (
      highestPriorityStatus === null ||
      THREAD_STATUS_PRIORITY[status.label] > THREAD_STATUS_PRIORITY[highestPriorityStatus.label]
    ) {
      highestPriorityStatus = status;
    }
  }
  return highestPriorityStatus;
}
